/**
 * NotFound.tsx — 404 page.
 *
 * Shown when a route doesn't match.
 * "Go Back" + "Go to Dashboard" (authenticated) or "Go to Homepage" (guest).
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DashboardIcon from '@mui/icons-material/Dashboard';
import HomeIcon from '@mui/icons-material/Home';
import SecurityIcon from '@mui/icons-material/Security';

import { useAuth } from '../context/AuthContext';

export default function NotFound() {
  const navigate = useNavigate();
  const { user }  = useAuth();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        px: 3,
        textAlign: 'center',
      }}
    >
      {/* Logo */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 5 }}>
        <SecurityIcon sx={{ color: '#00E5FF', fontSize: 32, filter: 'drop-shadow(0 0 6px rgba(0,229,255,0.6))' }} />
        <Typography variant="h5" fontWeight={900} sx={{ color: '#00E5FF', textShadow: '0 0 12px rgba(0,229,255,0.4)' }}>Shield</Typography>
      </Box>

      {/* 404 */}
      <Typography
        sx={{
          fontSize: { xs: '5rem', sm: '8rem', md: '10rem' },
          fontWeight: 900,
          lineHeight: 1,
          color: 'primary.main',
          mb: 2,
          letterSpacing: '-0.04em',
        }}
      >
        404
      </Typography>

      <Typography variant="h5" fontWeight={700} mb={1.5}>
        Page not found
      </Typography>
      <Typography variant="body1" color="text.secondary" mb={5} sx={{ maxWidth: 420 }}>
        The page you're looking for doesn't exist or has been moved. Double-check the URL
        or use one of the options below.
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ fontWeight: 600 }}
        >
          Go Back
        </Button>

        {user ? (
          <Button
            variant="contained"
            startIcon={<DashboardIcon />}
            onClick={() => navigate('/dashboard')}
            sx={{ fontWeight: 700 }}
          >
            Go to Dashboard
          </Button>
        ) : (
          <Button
            variant="contained"
            startIcon={<HomeIcon />}
            onClick={() => navigate('/')}
            sx={{ fontWeight: 700 }}
          >
            Go to Homepage
          </Button>
        )}
      </Stack>
    </Box>
  );
}
